import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { Loader2, X } from 'src/components/common/icons';
import { Field } from './Master';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

const TinMatchingCsvDialog: React.FC<IProps> = ({ isOpen, onClose }) => {
    const { get, response } = useFetch();

    const [taxYear, setTaxYear] = useState('');
    const [clientNo, setClientNo] = useState('');
    const [projectNo, setProjectNo] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!taxYear) {
            setTaxYear(String(new Date().getFullYear() - 1));
        }
    }, [isOpen, taxYear]);

    const handleDownload = async (): Promise<void> => {
        if (!taxYear.trim()) {
            alert('Tax year is required.');
            return;
        }

        setIsGenerating(true);
        setRequestError(null);
        try {
            const params = new URLSearchParams();
            params.append('taxYear', taxYear.trim());
            params.append('clNo', clientNo.trim());
            params.append('prNo', projectNo.trim());

            await get(`/v1.0/leases/admin/1099/tin-matching/report?${params.toString()}`);
            if (!response.ok) {
                setRequestError(
                    parseLeaseRecordsApiError(response.data || { title: 'Failed to generate TIN Matching CSV report.' }, response.status)
                );
                return;
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `TINMatching_${taxYear.trim()}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error generating TIN Matching CSV report.' }, 500));
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.content} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h3 style={styles.title}>TIN Matching CSV</h3>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close TIN Matching dialog">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={styles.body}>
                    <div style={styles.fieldGrid}>
                        <Field label="Tax Year" value={taxYear} editable={true} onChange={(value) => setTaxYear(value || '')} />
                        <Field label="Client No. (Optional)" value={clientNo} editable={true} onChange={(value) => setClientNo(value || '')} />
                        <Field label="Project No. (Optional)" value={projectNo} editable={true} onChange={(value) => setProjectNo(value || '')} />
                    </div>
                </div>

                <div style={styles.footer}>
                    <button onClick={onClose} style={styles.secondaryButton} disabled={isGenerating}>
                        Cancel
                    </button>
                    <button onClick={handleDownload} style={styles.primaryButton} disabled={isGenerating}>
                        <div style={styles.buttonInner}>
                            {isGenerating && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                            <span>{isGenerating ? 'Generating...' : 'Download CSV'}</span>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        padding: '1rem',
    },
    content: {
        width: 'min(600px, 96vw)',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.75rem',
        boxShadow: '0 24px 48px rgba(17, 24, 39, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1rem',
        borderBottom: '1px solid #E5E7EB',
    },
    title: {
        margin: 0,
        fontSize: '1.1rem',
        fontWeight: 700,
        color: '#1F2937',
    },
    closeButton: {
        border: 'none',
        background: 'transparent',
        color: '#6B7280',
        cursor: 'pointer',
        padding: '0.2rem',
    },
    body: {
        padding: '0.9rem',
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '0.7rem',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.6rem',
        padding: '0.9rem 1rem',
        borderTop: '1px solid #E5E7EB',
    },
    buttonInner: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
    },
    primaryButton: {
        border: 'none',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#1D4ED8',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
    },
    secondaryButton: {
        border: '1px solid #CBD5E1',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#FFFFFF',
        color: '#0F172A',
        cursor: 'pointer',
        fontWeight: 600,
    },
};

export default TinMatchingCsvDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/TinMatchingCsvDialog.tsx